import moment from 'moment';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import { EMAIL_REGEX } from '../../../shared/constants/Constants';
import {
  CRUISE_AGE_LIMITS,
  NAME_REGEX,
  PAN_REGEX,
  PASSPORT_REGEX,
  PIN_REGEX,
} from '../constants/cruiseConstants';
import { API_DATE } from './cruiseUtils';

const blank = (v) => v === undefined || v === null || String(v).trim() === '';

const ADDRESS_FIELDS = ['address', 'city', 'state', 'country', 'pinCode'];

/** Message for an empty required field, worded for that field. */
const REQUIRED = {
  salutation: 'Select a title',
  firstName: 'Enter first name',
  lastName: 'Enter last name',
  gender: 'Select gender',
  dateOfBirth: 'Select date of birth',
  nationality: 'Enter nationality',
  passportNumber: 'Enter passport number',
  passportExpiry: 'Select passport expiry date',
  passportIssuePlace: 'Select place of issue',
  email: 'Enter email address',
  countryIso: 'Select mobile country code',
  phoneNo: 'Enter mobile number',
  address: 'Enter street address',
  city: 'Enter city',
  state: 'Enter state',
  country: 'Enter country',
  pinCode: 'Enter PIN code',
};

const TYPE_NAME = { adult: 'An adult', child: 'A child', infant: 'An infant' };

const sailMoment = (sailDate) => {
  const m = moment(sailDate, API_DATE, true);
  return m.isValid() ? m : moment();
};

/**
 * Age on the sailing date, in the unit the guest type is ruled by (years
 * for adults / children, months for infants).
 */
const ageOnSailing = (dob, sailDate, unit) => {
  const birth = moment(dob, API_DATE, true);
  if (!birth.isValid()) return null;
  return sailMoment(sailDate).diff(birth, unit);
};

const describeLimit = (type) => {
  const { min, max, unit } = CRUISE_AGE_LIMITS[type];
  if (type === 'adult') return `${min} years or older`;
  return `${min}–${max} ${unit} old`;
};

/**
 * Dates of birth the picker allows for a guest type, so an out-of-range
 * date can't be picked in the first place (QA: a 2024 DOB for an adult).
 */
export const dobRange = (type, sailDate) => {
  const sail = sailMoment(sailDate);
  const { min, max, unit } = CRUISE_AGE_LIMITS[type];
  const today = moment().endOf('day');
  const latest = moment.min(sail.clone().subtract(min, unit), today);
  const earliest = sail
    .clone()
    .subtract(max + 1, unit)
    .add(1, 'day');
  return {
    minDate: earliest.toDate(),
    maxDate: latest.toDate(),
    openToDate: latest.toDate(),
  };
};

/**
 * Field errors for one guest, each with a message for that field.
 *
 * Lead guest: contact and address are required — the line sends the
 * confirmation there. Other guests: contact optional (checked if filled),
 * address required only when "same address as lead" is off.
 */
export const validateGuest = (g, { lead, needsAddress, sailDate, endDate }) => {
  const e = {};
  const req = (field) => {
    if (blank(g[field])) e[field] = REQUIRED[field] || 'This field is required';
  };

  [
    'salutation',
    'firstName',
    'lastName',
    'gender',
    'dateOfBirth',
    'nationality',
    'passportNumber',
    'passportExpiry',
  ].forEach(req);

  [
    ['firstName', 'First name'],
    ['middleName', 'Middle name'],
    ['lastName', 'Last name'],
  ].forEach(([f, label]) => {
    if (!blank(g[f]) && !NAME_REGEX.test(g[f])) {
      e[f] = `${label} can only have letters and spaces`;
    }
  });
  if (!blank(g.firstName) && !e.firstName && g.firstName.trim().length < 2) {
    e.firstName = 'First name must be at least 2 letters';
  }
  if (!blank(g.lastName) && !e.lastName && g.lastName.trim().length < 2) {
    e.lastName = 'Last name must be at least 2 letters';
  }

  if (!blank(g.dateOfBirth)) {
    const { min, max, unit } = CRUISE_AGE_LIMITS[g.paxType];
    const age = ageOnSailing(g.dateOfBirth, sailDate, unit);
    if (age === null || age < min || age > max) {
      e.dateOfBirth = `${TYPE_NAME[g.paxType]} must be ${describeLimit(
        g.paxType,
      )} on the sailing date`;
    }
  }

  if (!blank(g.passportNumber) && !PASSPORT_REGEX.test(g.passportNumber)) {
    e.passportNumber = 'Passport number must be 6–12 letters or digits';
  }
  if (!blank(g.passportExpiry)) {
    const exp = moment(g.passportExpiry, API_DATE, true);
    const end = moment(endDate || sailDate, API_DATE, true);
    if (!exp.isValid() || (end.isValid() && !exp.isAfter(end))) {
      e.passportExpiry = 'Passport must be valid beyond the return date';
    }
  }
  if (!blank(g.panNumber) && !PAN_REGEX.test(g.panNumber)) {
    e.panNumber = 'PAN must look like ABCDE1234F';
  }

  if (lead) {
    req('email');
    req('countryIso');
    req('phoneNo');
  }
  if (!blank(g.email) && !EMAIL_REGEX.test(g.email.trim())) {
    e.email = 'Enter a valid email address, e.g. name@example.com';
  }
  if (!blank(g.phoneNo)) {
    let valid = false;
    try {
      valid = isValidPhoneNumber(g.phoneNo, g.countryIso || 'IN');
    } catch (err) {
      valid = false;
    }
    if (!valid) {
      e.phoneNo = `Enter a valid mobile number for ${
        g.countryCode || 'the selected country'
      }`;
    }
  }

  if (needsAddress) {
    ADDRESS_FIELDS.forEach(req);
    if (!blank(g.pinCode) && !PIN_REGEX.test(g.pinCode)) {
      e.pinCode = 'PIN code must be 3–10 letters or digits';
    }
  }

  return e;
};

export const hasErrors = (errorsList) =>
  errorsList.some((e) => Object.keys(e).length > 0);
